import { type InjectedFormikProps, withFormik } from "formik";
import { Component, type FunctionComponent } from "react";

import { BTSelectItem } from "types/apiResponse/apiResponse";

import yup from "utilities/form/yup";
import { isNullOrWhitespace } from "utilities/string/string";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTForm, BTFormItem, BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTLayoutContent } from "commonComponents/btWrappers/BTLayout/BTLayout";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTModalLayout } from "commonComponents/btWrappers/BTModal/BTModalLayout";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { BTSelectUser } from "commonComponents/btWrappers/BTSelectUser/BTSelectUser";
import { BTTable } from "commonComponents/btWrappers/BTTable/BTTable";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { DateDisplay } from "commonComponents/utilities/DateDisplay/DateDisplay";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";
import { FormikValidationSummary } from "commonComponents/utilities/validationSummary/FormikValidationSummary/FormikValidationSummary";

import { getStatusText } from "entity/bill/common/BillStatus/BillStatus";
import { PurchaseOrderVerifyWorkStatusTag } from "entity/purchaseOrder/common/PurchaseOrderVerifyWorkStatusTag";
import { RfiStatusTag } from "entity/rfi/RfiStatusTag/RfiStatusTag";
import { WarrantyServiceStatus } from "entity/warranty/common/WarrantyServiceStatus/WarrantyServiceStatus";

import {
    type BillConflict,
    type DocumentConflict,
    DocumentJobAssignment,
    EntityJobAssignment,
    type IInternalUserDeleteConflictsFormValues,
    type InternalUserDeleteConflictsEntity,
    type InternalUserDeleteConflictsFormActions,
    type LeadActivityConflict,
    type LeadConflict,
    type LeadProposalTemplateConflict,
    type PurchaseOrderConflict,
    type PurchaseOrderJobConflict,
    purchaseOrderJobLimit,
    type RfiConflict,
    type WarrantyServiceConflict,
} from "./InternalUserDeleteConflicts.api.types";

export interface IInternalUserDeleteConflictsProps {
    loginId: number;
    internalUserName: string;
    entity: InternalUserDeleteConflictsEntity;
    onReassignJobsitesForUserDeletion: (newLoginId: number) => Promise<void>;
    onReassignItemsForUserDeletion: (
        formValues: IInternalUserDeleteConflictsFormValues,
    ) => Promise<void>;
    actionBeingPerformed: InternalUserDeleteConflictsFormActions;
    modalConfig?: IModalConfiguration;
}

const getPurchaseOrderJobsSchema = (poJobIndex: number, key: keyof EntityJobAssignment) => {
    const field: keyof InternalUserDeleteConflictsEntity = "purchaseOrderJobs";
    return `${field}[${poJobIndex}].${key}`;
};

const getWarrantyServiceJobsSchema = (wsJobIndex: number, key: keyof EntityJobAssignment) => {
    const field: keyof InternalUserDeleteConflictsEntity = "warrantyServiceJobs";
    return `${field}[${wsJobIndex}].${key}`;
};

const getDocumentJobsSchema = (docJobIndex: number, key: keyof DocumentJobAssignment) => {
    const field: keyof InternalUserDeleteConflictsEntity = "documentJobs";
    return `${field}[${docJobIndex}].${key}`;
};

const NoAvailableUsersAlertBanner: React.FC<InternalUserDeleteConflictsEntity> = (entity) => {
    // The select lists by default are populated with a default select item for "NONE",
    // so if the users list contains a single item, then only the default select item is available.
    const jobsitesNoAvailableUsers =
        entity.jobsites.length > 0 && entity.usersWithJobViewing.length === 1;
    const leadsNoAvailableUsers =
        entity.leads.length > 0 && entity.usersWithLeadViewing.length === 1;
    const leadActivitiesNoAvailableUsers =
        entity.leadActivities.length > 0 && entity.usersWithLeadViewing.length === 1;
    const leadProposalTemplatesNoAvailableUsers =
        entity.leadProposalTemplates.length > 0 && entity.usersWithLeadViewing.length === 1;
    const rfisNoAvailableUsers = entity.rfis.length > 0 && entity.usersWithRfiViewing.length === 1;
    const contactFormDefaultUserNoAvailableUsers =
        entity.isContactFormDefaultUser && entity.usersThatCanReassignSalesperson.length === 1;
    const accountingUserNoAvailableUsers =
        entity.isAccountingUser && entity.usersWithAdminUpdatePermission.length === 1;

    // POs are handled differently and are mass reassigned if there are over a certain number of PO jobs.
    // Otherwise if they are individually reassigned we have to check available users on a per job basis.
    let purchaseOrdersNoAvailableUsers: boolean = false;
    const { purchaseOrderJobs, documentJobs } = entity;
    if (purchaseOrderJobs.length > purchaseOrderJobLimit) {
        // This is a list of users, NOT a list of BTSelectItems, so check for length 0 on this.
        purchaseOrdersNoAvailableUsers = entity.users.length === 0;
    } else if (purchaseOrderJobs.length > 0) {
        purchaseOrdersNoAvailableUsers = purchaseOrderJobs.some(
            (poJob) => poJob.associatedLogins.length === 1,
        );
    }

    const warrantyServiceNoAvailableUsers =
        entity.warrantyServiceJobs.length > 0 &&
        entity.warrantyServiceJobs.some((wsJob) => wsJob.associatedLogins.length === 1);

    let documentsNoAvailableUsers: boolean = false;
    if (documentJobs.length > 0) {
        documentsNoAvailableUsers = documentJobs.some(
            (docJob) => docJob.associatedLogins.length === 1,
        );
    }

    // If all of the entities that need to be reassigned have users to reassign to,
    // then return early and don't display the error banner.
    if (
        !(
            jobsitesNoAvailableUsers ||
            purchaseOrdersNoAvailableUsers ||
            leadsNoAvailableUsers ||
            leadActivitiesNoAvailableUsers ||
            leadProposalTemplatesNoAvailableUsers ||
            rfisNoAvailableUsers ||
            contactFormDefaultUserNoAvailableUsers ||
            accountingUserNoAvailableUsers ||
            documentsNoAvailableUsers ||
            warrantyServiceNoAvailableUsers
        )
    ) {
        return null;
    }

    return (
        <BTAlert
            showIcon
            banner
            type="error"
            message={
                <>
                    There are no available users with permissions to update the following.
                    <ul>
                        {jobsitesNoAvailableUsers && <li>Jobsites</li>}
                        {purchaseOrdersNoAvailableUsers && <li>Purchase Orders</li>}
                        {leadsNoAvailableUsers && <li>Leads</li>}
                        {documentsNoAvailableUsers && <li>Documents</li>}
                        {leadProposalTemplatesNoAvailableUsers && <li>Lead Proposal Templates</li>}
                        {leadActivitiesNoAvailableUsers && <li>Lead Activities</li>}
                        {rfisNoAvailableUsers && <li>RFIs</li>}
                        {contactFormDefaultUserNoAvailableUsers && <li>Lead Contact Form</li>}
                        {accountingUserNoAvailableUsers && <li>Accounting System Setup</li>}
                        {warrantyServiceNoAvailableUsers && <li>Warranty Service Appointments</li>}
                    </ul>
                    Grant another user with these permissions and try again.
                </>
            }
            data-testid="noAvailableUsersWithPermissions"
        />
    );
};

class InternalUserDeleteConflictsInternal extends Component<
    InjectedFormikProps<IInternalUserDeleteConflictsProps, IInternalUserDeleteConflictsFormValues>
> {
    render() {
        const { actionBeingPerformed, handleSubmit, values, errors, internalUserName } = this.props;
        return (
            <BTModalLayout
                title="Internal User Delete Conflicts"
                footerContent={
                    <BTButtonDeprecated<InternalUserDeleteConflictsFormActions>
                        type="primary"
                        data-testid="submitting"
                        onClick={handleSubmit}
                        actionBeingPerformed={actionBeingPerformed}
                        loadingAction="submitting"
                    >
                        {this.props.entity.jobsites.length === 0
                            ? "Transfer Permissions"
                            : "Save & Continue"}
                    </BTButtonDeprecated>
                }
                modalConfig={this.props.modalConfig}
            >
                <BTForm>
                    <BTLayoutContent>
                        <FormikValidationSummary
                            errors={errors}
                            values={values}
                            showAfterSubmit={this.props.submitCount}
                            scheme={getInternalUserDeleteConflictsValidators(this.props)}
                        />

                        <NoAvailableUsersAlertBanner {...this.props.entity} />

                        <PageSection title="Users Conflicts Preventing Deletion">
                            There are jobs, docs, POs, warranty service appointments, RFIs, leads,
                            lead proposal templates, lead activities and/or lead emails currently
                            that ONLY <strong>{internalUserName}</strong> has access to. In order to
                            proceed with delete, you'll need to choose a user who can access these
                            items.
                        </PageSection>

                        {this.props.entity.jobsites.length > 0 && (
                            <ConflictsWithJobsToReplace {...this.props} />
                        )}
                        {this.props.entity.jobsites.length === 0 && (
                            <ConflictsWithoutJobsToReplace {...this.props} />
                        )}
                    </BTLayoutContent>
                </BTForm>
            </BTModalLayout>
        );
    }
}

class ConflictsWithJobsToReplace extends Component<
    InjectedFormikProps<IInternalUserDeleteConflictsProps, IInternalUserDeleteConflictsFormValues>
> {
    render() {
        const { setFieldValue, values } = this.props;
        const { jobsites, usersWithJobViewing } = this.props.entity;

        const jobList = (
            <div>
                <BTTitle>Jobs</BTTitle>
                <ul>
                    {jobsites.map((job, index) => (
                        <li key={index}>{job.jobName}</li>
                    ))}
                </ul>
            </div>
        );

        return (
            <PageSection title="Reassign Jobs">
                {jobList}
                <BTRow>
                    <BTCol xs={24} sm={12}>
                        <BTFormItemAutomatic<IInternalUserDeleteConflictsFormValues> id="assignJobToId">
                            <BTSelectUser<IInternalUserDeleteConflictsFormValues>
                                id="assignJobToId"
                                data-testid="assignJobTo"
                                value={values.assignJobToId}
                                treeData={usersWithJobViewing}
                                onChange={setFieldValue}
                                onBlur={() => {}}
                            />
                        </BTFormItemAutomatic>
                    </BTCol>
                </BTRow>
            </PageSection>
        );
    }
}

class ConflictsWithoutJobsToReplace extends Component<
    InjectedFormikProps<IInternalUserDeleteConflictsProps, IInternalUserDeleteConflictsFormValues>
> {
    private handlePurchaseOrderOrJobAssignmentChange = (
        poJobIndex: number,
        field: string,
        value: string | undefined,
        userDisplayName: string,
    ) => {
        this.props.setFieldValue(
            field,
            this.props.values.purchaseOrderJobs!.map((poJob, index) => {
                if (index === poJobIndex) {
                    return new EntityJobAssignment(poJob.jobId, parseInt(value!), userDisplayName);
                }
                return poJob;
            }),
        );
    };

    private handleWarrantyServiceOrJobAssignmentChange = (
        wsJobIndex: number,
        field: string,
        value: string | undefined,
        userDisplayName: string,
    ) => {
        this.props.setFieldValue(
            field,
            this.props.values.warrantyServiceJobs!.map((wsJob, index) => {
                if (index === wsJobIndex) {
                    return new EntityJobAssignment(wsJob.jobId, parseInt(value!), userDisplayName);
                }
                return wsJob;
            }),
        );
    };

    private handleDocumentJobAssignmentChange = (
        docJobIndex: number,
        field: string,
        value: string | undefined,
        userDisplayName: string,
    ) => {
        this.props.setFieldValue(
            field,
            this.props.values.documentJobs!.map((docJob, index) => {
                if (index === docJobIndex) {
                    return new DocumentJobAssignment(
                        docJob.jobId,
                        parseInt(value!),
                        userDisplayName,
                    );
                }
                return docJob;
            }),
        );
    };

    render() {
        const {
            leads,
            leadActivities,
            leadProposalTemplates,
            purchaseOrderJobs,
            warrantyServiceJobs,
            rfis,
            isAccountingUser,
            isContactFormDefaultUser,
            documentJobs,
            bills,
        } = this.props.entity;

        const hasLeadsConflicts =
            leads.length > 0 || leadActivities.length > 0 || leadProposalTemplates.length > 0;

        const shouldMassAssignPOs = purchaseOrderJobs.length > purchaseOrderJobLimit;
        const shouldIndividuallyAssignPOs = !shouldMassAssignPOs && purchaseOrderJobs.length > 0;

        return (
            <>
                {shouldMassAssignPOs && this.props.values.purchaseOrderJobs !== null && (
                    <PurchaseOrdersConflictsMassReassign
                        {...this.props}
                        onPurchaseOrderOrDocumentJobAssignmentChange={
                            this.handlePurchaseOrderOrJobAssignmentChange
                        }
                    />
                )}
                {shouldIndividuallyAssignPOs && this.props.values.purchaseOrderJobs !== null && (
                    <PurchaseOrdersConflictsIndividual
                        {...this.props}
                        onPurchaseOrderOrDocumentJobAssignmentChange={
                            this.handlePurchaseOrderOrJobAssignmentChange
                        }
                    />
                )}
                {bills.length > 0 && <BillsConflicts {...this.props} />}
                {warrantyServiceJobs.length > 0 && (
                    <WarrantyServicesConflicts
                        {...this.props}
                        onWarrantyServiceOrAssignmentChange={
                            this.handleWarrantyServiceOrJobAssignmentChange
                        }
                    />
                )}
                {documentJobs.length > 0 && (
                    <DocumentConflicts
                        {...this.props}
                        onPurchaseOrderOrDocumentJobAssignmentChange={
                            this.handleDocumentJobAssignmentChange
                        }
                    />
                )}
                {hasLeadsConflicts && <LeadsConflicts {...this.props} />}
                {rfis.length > 0 && <RfisConflicts {...this.props} />}
                {isContactFormDefaultUser && <ContactFormConflicts {...this.props} />}
                {isAccountingUser && <AccountingConflicts {...this.props} />}
            </>
        );
    }
}

class DocumentConflicts extends Component<
    InjectedFormikProps<
        IPurchaseOrderDocumentConflictsProps,
        IInternalUserDeleteConflictsFormValues
    >
> {
    render() {
        const { values } = this.props;
        const { documentJobs } = this.props.entity;

        return (
            <PageSection title="Purchase Orders">
                {documentJobs.map((docJob, docJobIndex) => {
                    return (
                        <div key={`job-${docJob.jobId}`} className="padding-bottom-lg">
                            <BTFormItem label="Job">{docJob.jobName}</BTFormItem>
                            <BTTable<DocumentConflict>
                                rowKey={(po) => po.documentInstanceId.toString()}
                                dataSource={docJob.documents}
                                columns={[
                                    {
                                        title: "Folder",
                                        dataIndex: "folderName",
                                    },
                                    {
                                        title: "Title",
                                        dataIndex: "title",
                                    },
                                ]}
                                footer={() => (
                                    <BTRow>
                                        <BTCol xs={24} sm={12}>
                                            <BTFormItemAutomatic
                                                id={getDocumentJobsSchema(docJobIndex, "newUserId")}
                                            >
                                                <BTSelect<IInternalUserDeleteConflictsFormValues>
                                                    id="purchaseOrderJobs"
                                                    data-testid="purchaseOrderJobs"
                                                    value={
                                                        values.documentJobs![docJobIndex].newUserId
                                                    }
                                                    treeData={docJob.associatedLogins}
                                                    onChange={(field, value, selectedItem) =>
                                                        this.props.onPurchaseOrderOrDocumentJobAssignmentChange(
                                                            docJobIndex,
                                                            field,
                                                            value,
                                                            selectedItem!.title,
                                                        )
                                                    }
                                                    onBlur={() => {}}
                                                />
                                            </BTFormItemAutomatic>
                                        </BTCol>
                                    </BTRow>
                                )}
                                pagination={false}
                            />
                        </div>
                    );
                })}
            </PageSection>
        );
    }
}

interface IPurchaseOrderDocumentConflictsProps extends IInternalUserDeleteConflictsProps {
    onPurchaseOrderOrDocumentJobAssignmentChange: (
        poDocumentIndex: number,
        field: string,
        value: string | undefined,
        userDisplayName: string,
    ) => void;
}

interface IWarrantyServiceConflictsProps extends IInternalUserDeleteConflictsProps {
    onWarrantyServiceOrAssignmentChange: (
        wsIndex: number,
        field: string,
        value: string | undefined,
        userDisplayName: string,
    ) => void;
}

class PurchaseOrdersConflictsIndividual extends Component<
    InjectedFormikProps<
        IPurchaseOrderDocumentConflictsProps,
        IInternalUserDeleteConflictsFormValues
    >
> {
    render() {
        const { values } = this.props;
        const { purchaseOrderJobs } = this.props.entity;

        return (
            <PageSection title="Purchase Orders">
                {purchaseOrderJobs.map((poJob, poJobIndex) => {
                    return (
                        <div key={`job-${poJob.jobId}`} className="padding-bottom-lg">
                            <BTFormItem label="Job">{poJob.jobName}</BTFormItem>
                            <BTTable<PurchaseOrderConflict>
                                rowKey={(po) => po.purchaseOrderId.toString()}
                                dataSource={poJob.purchaseOrders}
                                columns={[
                                    {
                                        title: "Title",
                                        dataIndex: "title",
                                    },
                                    {
                                        title: "Client Name",
                                        dataIndex: "ownerName",
                                    },
                                    {
                                        title: "Work Completed",
                                        dataIndex: "workStatus",
                                        render: (workStatus) => (
                                            <PurchaseOrderVerifyWorkStatusTag status={workStatus} />
                                        ),
                                    },
                                    {
                                        title: "Date Added",
                                        dataIndex: "dateAdded",
                                        render: (date) => <DateDisplay value={date} />,
                                    },
                                ]}
                                footer={() => (
                                    <BTRow>
                                        <BTCol xs={24} sm={12}>
                                            <BTFormItemAutomatic
                                                id={getPurchaseOrderJobsSchema(
                                                    poJobIndex,
                                                    "newUserId",
                                                )}
                                            >
                                                <BTSelect<IInternalUserDeleteConflictsFormValues>
                                                    id="purchaseOrderJobs"
                                                    data-testid="purchaseOrderJobs"
                                                    value={
                                                        values.purchaseOrderJobs![poJobIndex]
                                                            .newUserId
                                                    }
                                                    treeData={poJob.associatedLogins}
                                                    onChange={(field, value, selectedItem) =>
                                                        this.props.onPurchaseOrderOrDocumentJobAssignmentChange(
                                                            poJobIndex,
                                                            field,
                                                            value,
                                                            selectedItem!.title,
                                                        )
                                                    }
                                                    onBlur={() => {}}
                                                />
                                            </BTFormItemAutomatic>
                                        </BTCol>
                                    </BTRow>
                                )}
                                pagination={false}
                            />
                        </div>
                    );
                })}
            </PageSection>
        );
    }
}

class PurchaseOrdersConflictsMassReassign extends Component<
    InjectedFormikProps<
        IPurchaseOrderDocumentConflictsProps,
        IInternalUserDeleteConflictsFormValues
    >
> {
    render() {
        const { values } = this.props;
        const { purchaseOrderJobs, users } = this.props.entity;

        const totalPOs = purchaseOrderJobs.reduce(
            (prev, curr) => prev + curr.purchaseOrders.length,
            0,
        );
        const distinctUsersOnJobs = new Set<number>();
        purchaseOrderJobs.forEach((x) => {
            x.associatedLogins.forEach((y) => {
                if (!distinctUsersOnJobs.has(y.value)) {
                    distinctUsersOnJobs.add(y.value);
                }
            });
        });
        // When mass reassigning POs, don't worry about restricting by which users have access to which jobs.
        // If a user selected to be assigned to a POs on a job doesn't have access to that job, the
        // save request will return a new set of delete conflicts and the modal will refresh with those conflicts.
        const usersSelectList = [
            new BTSelectItem({ title: "-- NONE --", value: -1 }),
            ...users
                .filter((x) => distinctUsersOnJobs.has(x.loginId))
                .map((user) => user.toBTSelectItem()),
        ];

        return (
            <PageSection title="Purchase Orders">
                {purchaseOrderJobs.length} Jobs contains {totalPOs} Purchase Orders
                <BTTable<PurchaseOrderJobConflict>
                    className="padding-top-md"
                    rowKey={(poJob) => poJob.jobId.toString()}
                    dataSource={purchaseOrderJobs}
                    columns={[
                        {
                            title: "Job Name",
                            dataIndex: "jobName",
                        },
                        {
                            title: "Purchase Orders",
                            dataIndex: "purchaseOrders",
                            render: (purchaseOrders) => purchaseOrders.length,
                            align: "right",
                        },
                    ]}
                    footer={() => (
                        <BTRow>
                            <BTCol xs={24} sm={12}>
                                <BTFormItemAutomatic
                                    id={getPurchaseOrderJobsSchema(0, "newUserId")}
                                    label="Assign all purchase orders to"
                                >
                                    <BTSelect<IInternalUserDeleteConflictsFormValues>
                                        id="purchaseOrderJobs"
                                        data-testid="purchaseOrderJobs"
                                        value={values.purchaseOrderJobs![0].newUserId}
                                        treeData={usersSelectList}
                                        onChange={(field, value, selectedItem) =>
                                            this.props.onPurchaseOrderOrDocumentJobAssignmentChange(
                                                0,
                                                field,
                                                value,
                                                selectedItem!.title,
                                            )
                                        }
                                        onBlur={() => {}}
                                    />
                                </BTFormItemAutomatic>
                            </BTCol>
                        </BTRow>
                    )}
                    pagination={false}
                />
            </PageSection>
        );
    }
}

const getWarrantyServiceTableFooter = (
    wsJobIndex: number,
    newUserId: number,
    associatedLogins: BTSelectItem[],
    onChange: (
        wsJobIndex: number,
        field: string,
        value: string | undefined,
        userDisplayName: string,
    ) => void,
) => {
    return () => (
        <BTRow>
            <BTCol xs={24} sm={12}>
                <BTFormItemAutomatic id={getWarrantyServiceJobsSchema(wsJobIndex, "newUserId")}>
                    <BTSelect<IInternalUserDeleteConflictsFormValues>
                        id="warrantyServiceJobs"
                        data-testid="warrantyServiceJobs"
                        value={newUserId}
                        treeData={associatedLogins}
                        onChange={(field, value, selectedItem) =>
                            onChange(wsJobIndex, field, value, selectedItem!.title)
                        }
                        onBlur={() => {}}
                    />
                </BTFormItemAutomatic>
            </BTCol>
        </BTRow>
    );
};

const WarrantyServicesConflicts: FunctionComponent<
    InjectedFormikProps<IWarrantyServiceConflictsProps, IInternalUserDeleteConflictsFormValues>
> = ({ values, entity, onWarrantyServiceOrAssignmentChange }) => {
    const { warrantyServiceJobs } = entity;

    return (
        <PageSection title="Warranty Service Appointments">
            {warrantyServiceJobs.map((wsJob, wsJobIndex) => {
                return (
                    <div key={`job-${wsJob.jobId}`} className="padding-bottom-lg">
                        <BTFormItem label="Job">{wsJob.jobName}</BTFormItem>
                        <BTTable<WarrantyServiceConflict>
                            rowKey="warrantyServiceId"
                            dataSource={wsJob.warrantyServices}
                            columns={[
                                {
                                    title: "Warranty",
                                    dataIndex: "warrantyTitle",
                                },
                                {
                                    title: "Classification",
                                    dataIndex: "warrantyClassification",
                                },
                                {
                                    title: "Appointment Date",
                                    dataIndex: "appointmentDate",
                                    render: (date) => <DateDisplay showTime={true} value={date} />,
                                },
                                {
                                    title: "Status",
                                    dataIndex: "serviceStatus",
                                    render: (serviceStatus) => (
                                        <WarrantyServiceStatus
                                            status={serviceStatus.subAckStatusAsEnum}
                                            hoursUntilAppointmentDate={
                                                serviceStatus.hoursUntilAppointmentDate
                                            }
                                            assignedToLoginType={serviceStatus.performedByLoginType}
                                            scheduledDateAccepted={
                                                !serviceStatus.subNeedsToSetAppointment
                                            }
                                            acknowledgedDate={
                                                serviceStatus.performingUserAcceptedDate
                                            }
                                        />
                                    ),
                                },
                            ]}
                            pagination={false}
                            footer={getWarrantyServiceTableFooter(
                                wsJobIndex,
                                values.warrantyServiceJobs![wsJobIndex].newUserId,
                                wsJob.associatedLogins,
                                onWarrantyServiceOrAssignmentChange,
                            )}
                        />
                    </div>
                );
            })}
        </PageSection>
    );
};

class LeadsConflicts extends Component<
    InjectedFormikProps<IInternalUserDeleteConflictsProps, IInternalUserDeleteConflictsFormValues>
> {
    render() {
        const { values, setFieldValue } = this.props;
        const { leads, leadProposalTemplates, leadActivities, usersWithLeadViewing } =
            this.props.entity;

        return (
            <PageSection title="Leads">
                {leads.length > 0 && (
                    <div>
                        <BTTitle>Leads</BTTitle>
                        <BTTable<LeadConflict>
                            dataSource={leads}
                            rowKey={(lead) => lead.leadId.toString()}
                            columns={[
                                {
                                    title: "Lead Name",
                                    dataIndex: "opportunityTitle",
                                },
                                {
                                    title: "Date Added",
                                    dataIndex: "dateAdded",
                                    render: (date) => <DateDisplay value={date} />,
                                },
                            ]}
                            footer={() => (
                                <BTRow>
                                    <BTCol xs={24} sm={12}>
                                        <BTFormItemAutomatic<IInternalUserDeleteConflictsFormValues> id="newLeadAssignedUserId">
                                            <BTSelectUser<IInternalUserDeleteConflictsFormValues>
                                                id="newLeadAssignedUserId"
                                                data-testid="leadAssignedUser"
                                                value={values.newLeadAssignedUserId}
                                                treeData={usersWithLeadViewing}
                                                onChange={setFieldValue}
                                                onBlur={() => {}}
                                            />
                                        </BTFormItemAutomatic>
                                    </BTCol>
                                </BTRow>
                            )}
                            pagination={false}
                        />
                    </div>
                )}

                {leadProposalTemplates.length > 0 && (
                    <div className="padding-top-lg">
                        <BTTitle>Lead Proposal Templates</BTTitle>
                        <BTTable<LeadProposalTemplateConflict>
                            dataSource={leadProposalTemplates}
                            rowKey={(proposal) => proposal.proposalId.toString()}
                            columns={[
                                {
                                    title: "Template Name",
                                    dataIndex: "title",
                                },
                                {
                                    title: "Date Added",
                                    dataIndex: "addedByDate",
                                    render: (date) => <DateDisplay value={date} />,
                                },
                            ]}
                            footer={() => (
                                <BTRow>
                                    <BTCol xs={24} sm={12}>
                                        <BTFormItemAutomatic<IInternalUserDeleteConflictsFormValues> id="newLeadProposalAssignedUserId">
                                            <BTSelectUser<IInternalUserDeleteConflictsFormValues>
                                                id="newLeadProposalAssignedUserId"
                                                data-testid="leadProposalAssignedUse"
                                                value={values.newLeadProposalAssignedUserId}
                                                treeData={usersWithLeadViewing}
                                                onChange={setFieldValue}
                                                onBlur={() => {}}
                                            />
                                        </BTFormItemAutomatic>
                                    </BTCol>
                                </BTRow>
                            )}
                            pagination={false}
                        />
                    </div>
                )}

                {leadActivities.length > 0 && (
                    <div className="padding-top-lg">
                        <BTTitle>Lead Activities</BTTitle>
                        <BTTable<LeadActivityConflict>
                            dataSource={leadActivities}
                            rowKey={(activity) => activity.activityId.toString()}
                            columns={[
                                {
                                    title: "Lead Name",
                                    dataIndex: "opportunityTitle",
                                },
                                {
                                    title: "Activity Type",
                                    dataIndex: "activityType",
                                },
                                {
                                    title: "Scheduled Date",
                                    dataIndex: "scheduledDate",
                                    render: (date) => <DateDisplay value={date} />,
                                },
                            ]}
                            footer={() => (
                                <BTRow>
                                    <BTCol xs={24} sm={12}>
                                        <BTFormItemAutomatic<IInternalUserDeleteConflictsFormValues> id="newLeadActivityAssignedUserId">
                                            <BTSelectUser<IInternalUserDeleteConflictsFormValues>
                                                id="newLeadActivityAssignedUserId"
                                                data-testid="leadActivityAssignedUser"
                                                value={values.newLeadActivityAssignedUserId}
                                                treeData={usersWithLeadViewing}
                                                onChange={setFieldValue}
                                                onBlur={() => {}}
                                            />
                                        </BTFormItemAutomatic>
                                    </BTCol>
                                </BTRow>
                            )}
                            pagination={false}
                        />
                    </div>
                )}
            </PageSection>
        );
    }
}

class RfisConflicts extends Component<
    InjectedFormikProps<IInternalUserDeleteConflictsProps, IInternalUserDeleteConflictsFormValues>
> {
    render() {
        const { values, setFieldValue } = this.props;
        const { rfis, usersWithRfiViewing } = this.props.entity;

        return (
            <PageSection title="RFIs">
                <BTTitle>RFIs</BTTitle>
                <BTTable<RfiConflict>
                    dataSource={rfis}
                    rowKey={(rfi) => rfi.rfiId.toString()}
                    columns={[
                        {
                            title: "Title",
                            dataIndex: "title",
                            render: (title, rfi) =>
                                !isNullOrWhitespace(rfi.jobPrefix)
                                    ? `${rfi.jobPrefix} - ${rfi.titlePrefix} - ${title}`
                                    : `${rfi.titlePrefix} - ${title}`,
                        },
                        {
                            title: "Status",
                            dataIndex: "status",
                            render: (rfiStatus) => <RfiStatusTag status={rfiStatus} />,
                        },
                        {
                            title: "Assigned To",
                            dataIndex: "assignedName",
                        },
                        {
                            title: "Due Date",
                            dataIndex: "dueDate",
                            render: (date) => <DateDisplay value={date} />,
                        },
                    ]}
                    footer={() => (
                        <BTRow>
                            <BTCol xs={24} sm={12}>
                                <BTFormItemAutomatic<IInternalUserDeleteConflictsFormValues> id="newRfiAssignedUserId">
                                    <BTSelectUser<IInternalUserDeleteConflictsFormValues>
                                        id="newRfiAssignedUserId"
                                        data-testid="rfiAssignedUser"
                                        value={values.newRfiAssignedUserId}
                                        treeData={usersWithRfiViewing}
                                        onChange={setFieldValue}
                                        onBlur={() => {}}
                                    />
                                </BTFormItemAutomatic>
                            </BTCol>
                        </BTRow>
                    )}
                    pagination={false}
                />
            </PageSection>
        );
    }
}

class BillsConflicts extends Component<
    InjectedFormikProps<IInternalUserDeleteConflictsProps, IInternalUserDeleteConflictsFormValues>
> {
    render() {
        const { values, setFieldValue } = this.props;
        const { bills, usersWithBillViewing } = this.props.entity;

        return (
            <PageSection title="Bills">
                <BTTitle>Bills</BTTitle>
                <BTTable<BillConflict>
                    dataSource={bills}
                    rowKey={(bill) => bill.billId.toString()}
                    columns={[
                        {
                            title: "Title",
                            dataIndex: "title",
                        },
                        {
                            title: "Status",
                            dataIndex: "paymentStatus",
                            render: (paymentStatus, bill) =>
                                getStatusText(paymentStatus, bill.isSubRequested),
                        },
                        {
                            title: "Date Added",
                            dataIndex: "dateAdded",
                            render: (date) => <DateDisplay value={date} />,
                        },
                    ]}
                    footer={() => (
                        <BTRow>
                            <BTCol xs={24} sm={12}>
                                <BTFormItemAutomatic<IInternalUserDeleteConflictsFormValues> id="newBillAssignedUserId">
                                    <BTSelectUser<IInternalUserDeleteConflictsFormValues>
                                        id="newBillAssignedUserId"
                                        data-testid="billAssignedUser"
                                        value={values.newBillAssignedUserId}
                                        treeData={usersWithBillViewing}
                                        onChange={setFieldValue}
                                        onBlur={() => {}}
                                    />
                                </BTFormItemAutomatic>
                            </BTCol>
                        </BTRow>
                    )}
                    pagination={false}
                />
            </PageSection>
        );
    }
}

class AccountingConflicts extends Component<
    InjectedFormikProps<IInternalUserDeleteConflictsProps, IInternalUserDeleteConflictsFormValues>
> {
    render() {
        const { values, setFieldValue } = this.props;
        const { usersWithAdminUpdatePermission } = this.props.entity;

        return (
            <PageSection title="Accounting System Setup">
                The login you are deleting is set as the default user for viewing/modifying the
                Accounting settings on the Setup page. Please assign a new default user.
                <BTRow className="padding-top-sm">
                    <BTCol xs={24} sm={12}>
                        <BTFormItemAutomatic<IInternalUserDeleteConflictsFormValues> id="newAccountingUserId">
                            <BTSelectUser<IInternalUserDeleteConflictsFormValues>
                                id="newAccountingUserId"
                                data-testid="accountingUser"
                                value={values.newAccountingUserId}
                                treeData={usersWithAdminUpdatePermission}
                                onChange={setFieldValue}
                                onBlur={() => {}}
                            />
                        </BTFormItemAutomatic>
                    </BTCol>
                </BTRow>
            </PageSection>
        );
    }
}

class ContactFormConflicts extends Component<
    InjectedFormikProps<IInternalUserDeleteConflictsProps, IInternalUserDeleteConflictsFormValues>
> {
    render() {
        const { values, setFieldValue } = this.props;
        const { usersThatCanReassignSalesperson } = this.props.entity;

        return (
            <PageSection title="Lead Contact Form">
                The login you are deleting is set as the default user for the Lead Contact Form.
                Please assign a new default user.
                <BTRow className="padding-top-sm">
                    <BTCol xs={24} sm={12}>
                        <BTFormItemAutomatic<IInternalUserDeleteConflictsFormValues> id="newLeadContactFormDefaultUserId">
                            <BTSelectUser<IInternalUserDeleteConflictsFormValues>
                                id="newLeadContactFormDefaultUserId"
                                data-testid="leadContactFormDefaultUser"
                                value={values.newLeadContactFormDefaultUserId}
                                treeData={usersThatCanReassignSalesperson}
                                onChange={setFieldValue}
                                onBlur={() => {}}
                            />
                        </BTFormItemAutomatic>
                    </BTCol>
                </BTRow>
            </PageSection>
        );
    }
}

const getInternalUserDeleteConflictsValidators = (props: IInternalUserDeleteConflictsProps) => {
    const hasJobsiteConflicts = props.entity.jobsites.length > 0;

    return yup.object().shape<IInternalUserDeleteConflictsFormValues>({
        oldUserId: yup.number(),
        assignJobToId: hasJobsiteConflicts
            ? yup.number().label("Assign job to").nullable(false).min(1, "Must select a user.")
            : yup.number().nullable(),
        purchaseOrderJobs:
            !hasJobsiteConflicts && props.entity.purchaseOrderJobs.length > 0
                ? yup.array<EntityJobAssignment>().of(
                      yup.object().shape({
                          jobId: yup.number().nullable(),
                          userDisplayName: yup.string(),
                          newUserId: yup
                              .number()
                              .label("Assign purchase orders to")
                              .nullable(false)
                              .min(1, "Must select a user."),
                      }),
                  )
                : yup.array<EntityJobAssignment>().nullable(),
        documentJobs:
            !hasJobsiteConflicts && props.entity.documentJobs.length > 0
                ? yup.array<DocumentJobAssignment>().of(
                      yup.object().shape({
                          jobId: yup.number().nullable(),
                          userDisplayName: yup.string(),
                          newUserId: yup
                              .number()
                              .label("Assign documents to")
                              .nullable(false)
                              .min(1, "Must select a user."),
                      }),
                  )
                : yup.array<DocumentJobAssignment>().nullable(),
        warrantyServiceJobs:
            !hasJobsiteConflicts && props.entity.warrantyServiceJobs.length > 0
                ? yup.array<EntityJobAssignment>().of(
                      yup.object().shape({
                          jobId: yup.number().nullable(),
                          userDisplayName: yup.string(),
                          newUserId: yup
                              .number()
                              .label("Assign warranty service appointments to")
                              .nullable(false)
                              .min(1, "Must select a user."),
                      }),
                  )
                : yup.array<EntityJobAssignment>().nullable(),
        // Leads are the only item that can be left unassigned
        newLeadAssignedUserId: yup.number().label("Assign leads to").nullable(),
        newLeadProposalAssignedUserId:
            !hasJobsiteConflicts && props.entity.leadProposalTemplates.length > 0
                ? yup
                      .number()
                      .label("Assign proposal templates to")
                      .nullable(false)
                      .min(1, "Must select a user.")
                : yup.number().nullable(),
        newLeadActivityAssignedUserId:
            !hasJobsiteConflicts && props.entity.leadActivities.length > 0
                ? yup
                      .number()
                      .label("Assign activities to")
                      .nullable(false)
                      .min(1, "Must select a user.")
                : yup.number().nullable(),
        newRfiAssignedUserId:
            !hasJobsiteConflicts && props.entity.rfis.length > 0
                ? yup.number().label("Assign RFIs to").nullable(false).min(1, "Must select a user.")
                : yup.number().nullable(),
        newLeadContactFormDefaultUserId:
            !hasJobsiteConflicts && props.entity.isContactFormDefaultUser
                ? yup
                      .number()
                      .label("Assign lead contact form default user to")
                      .nullable(false)
                      .min(1, "Must select a user.")
                : yup.number().nullable(),
        newAccountingUserId:
            !hasJobsiteConflicts && props.entity.isAccountingUser
                ? yup
                      .number()
                      .label("Assign lead contact form default user for accounting setup to")
                      .nullable(false)
                      .min(1, "Must select a user.")
                : yup.number().nullable(),
        newBillAssignedUserId:
            !hasJobsiteConflicts && props.entity.bills.length > 0
                ? yup
                      .number()
                      .label("Assign Bills to")
                      .nullable(false)
                      .min(1, "Must select a user.")
                : yup.number().nullable(),
    });
};

export const InternalUserDeleteConflictsPresentational = withFormik<
    IInternalUserDeleteConflictsProps,
    IInternalUserDeleteConflictsFormValues
>({
    // use the default values we specified above
    mapPropsToValues: (props: IInternalUserDeleteConflictsProps) => {
        let purchaseOrderJobs: EntityJobAssignment[] | null = null;
        if (props.entity.purchaseOrderJobs.length > purchaseOrderJobLimit) {
            purchaseOrderJobs = [new EntityJobAssignment(null, -1, "-- NONE --")];
        } else if (props.entity.purchaseOrderJobs.length > 0) {
            purchaseOrderJobs = props.entity.purchaseOrderJobs.map((poJob) => {
                return new EntityJobAssignment(poJob.jobId, -1, "-- NONE --");
            });
        }

        let warrantyServiceJobs: EntityJobAssignment[] | null = null;
        if (props.entity.warrantyServiceJobs.length > 0) {
            warrantyServiceJobs = props.entity.warrantyServiceJobs.map((wsJob) => {
                return new EntityJobAssignment(wsJob.jobId, -1, "-- NONE --");
            });
        }

        let documentJobs: DocumentJobAssignment[] | null = null;
        if (props.entity.documentJobs.length > 0) {
            documentJobs = props.entity.documentJobs.map((docJob) => {
                return new DocumentJobAssignment(docJob.jobId, -1, "-- NONE --");
            });
        }

        return {
            oldUserId: props.loginId,
            assignJobToId: props.entity.jobsites.length > 0 ? -1 : null,
            newRfiAssignedUserId: props.entity.rfis.length > 0 ? -1 : null,
            newLeadAssignedUserId: props.entity.leads.length > 0 ? -1 : null,
            newLeadProposalAssignedUserId:
                props.entity.leadProposalTemplates.length > 0 ? -1 : null,
            newLeadActivityAssignedUserId: props.entity.leadActivities.length > 0 ? -1 : null,
            newLeadContactFormDefaultUserId: props.entity.isContactFormDefaultUser ? -1 : null,
            newAccountingUserId: props.entity.isAccountingUser ? -1 : null,
            purchaseOrderJobs,
            warrantyServiceJobs,
            documentJobs,
            newBillAssignedUserId: props.entity.bills.length > 0 ? -1 : null,
        };
    },
    // use the yup validation object we specified above
    validationSchema: getInternalUserDeleteConflictsValidators,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,

    handleSubmit: async (
        values: IInternalUserDeleteConflictsFormValues,
        { props, setSubmitting },
    ) => {
        setSubmitting(true);
        if (props.entity.jobsites.length > 0) {
            await props.onReassignJobsitesForUserDeletion(values.assignJobToId!);
        } else {
            await props.onReassignItemsForUserDeletion(values);
        }
        setSubmitting(false);
    },
})(InternalUserDeleteConflictsInternal);
